"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
export default function FeaturedCaseStudies() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  const [ripples, setRipples] = useState<{
    [key: number]: {
      x: number;
      y: number;
      id: number;
    } | null;
  }>({});
  const caseStudies = [{
    id: 1,
    image: "https://famous-marmoset.10web.cloud/wp-content/uploads/2026/05/flexRnD-Durable-Goods.jpg?v=9jl3rvyqfn",
    title: "Advanced Materials Innovation",
    outcome: "Partnered with Manduka® to develop advanced materials technologies that enhanced the performance of the PRO®, GRP ADAPT®, and eKO® product lines, delivering improved grip, sustainability, durability, and user experience through innovative materials engineering."
  }, {
    id: 2,
    image: "https://famous-marmoset.10web.cloud/wp-content/uploads/2026/05/Stickables_align_01-scaled.jpg?v=dzc8jqy2t4u",
    title: "End to End Product Development",
    outcome: "Developed the decoration platform behind the Stickables® product line for Invisalign®, combining advanced materials engineering, adhesive technology, and scalable manufacturing to bring an innovative patient personalization solution from concept to commercial launch."
  }, {
    id: 3,
    image: "https://famous-marmoset.10web.cloud/wp-content/uploads/2026/05/flexRnD-Packaging.jpg?v=9qm5jfnubi",
    title: "Sustainable Packaging Solutions",
    outcome: "Partnered with leading consumer products companies to develop recyclable packaging, increase post-consumer recycled content, and reduce virgin plastic use. Highlights include a patented mono-material barrier film designed to replace traditional metallized packaging while enabling recyclable flexible packaging."
  }, {
    id: 4,
    image: "https://famous-marmoset.10web.cloud/wp-content/uploads/2026/07/CSP_Acquisition_Diligence.png?v=87zg290v0x",
    title: "Due Diligence for Strategic Acquisitions",
    outcome: "Supported Aptar's acquisition of CSP Technologies by leading technical due diligence across intellectual property, R&D, manufacturing, supply chain, and EHS. Our expertise in functional barrier packaging helped validate strategic value, identify risks, and support informed investment decisions."
  }, {
    id: 5,
    image: "https://famous-marmoset.10web.cloud/wp-content/uploads/2026/07/Mat_Wash_Products_crop.png?v=jzwvjukgzei",
    title: "Consumer Products for Brands",
    outcome: "Developed and produce the branded Mat Wash product line for Manduka®, creating a simple, effective cleaning solution designed to keep yoga mats clean without leaving behind unwanted residue. The formulation uses simple and natural ingredients, supporting both product performance and a clean consumer experience."
  }];
  const handleCardClick = (index: number, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = Date.now();
    setRipples(prev => ({
      ...prev,
      [index]: {
        x,
        y,
        id
      }
    }));
    setTimeout(() => {
      setRipples(prev => ({
        ...prev,
        [index]: null
      }));
    }, 600);
  };
  return <section ref={ref} className={`py-16 px-6 bg-light-background transition-opacity duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-sans font-bold text-foreground-light mb-4">
            Featured Success
          </h2>
          <p className="text-lg text-muted max-w-2xl mx-auto">
            Explore our proven track record of delivering innovative solutions that drive measurable results for emerging brands and established manufacturers.
          </p>
        </div>

        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full">
          <CarouselContent className="-ml-4">
            {caseStudies.map((study, i) => <CarouselItem key={study.id} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <div onClick={e => handleCardClick(i, e)} className={`cursor-pointer relative overflow-hidden transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
              transitionDelay: `${i * 100}ms`
            }}>
                  <Card className="h-full hover:shadow-strong hover:-translate-y-2 transition-all duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)]">
                    <img src={study.image} alt={study.title} className="w-full h-48 object-cover" />
                    <CardHeader>
                      <CardTitle className="text-xl text-foreground-light">
                        {study.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-muted leading-relaxed">
                        {study.outcome}
                      </CardDescription>
                    </CardContent>
                  </Card>

                  {ripples[i] && <span className="absolute pointer-events-none rounded-full bg-primary/30 animate-ping" style={{
                left: ripples[i]!.x,
                top: ripples[i]!.y,
                width: "20px",
                height: "20px",
                transform: "translate(-50%, -50%)",
                animationDuration: "600ms"
              }} />}
                </div>
              </CarouselItem>)}
          </CarouselContent>

          <div className="flex justify-center gap-4 mt-8">
            <CarouselPrevious className="relative inset-0 translate-x-0 translate-y-0 hover:bg-primary hover:text-white hover:shadow-[0_0_12px_hsl(215,80%,48%)] transition-all duration-[250ms]">
              <ChevronLeft className="w-5 h-5" />
            </CarouselPrevious>
            <CarouselNext className="relative inset-0 translate-x-0 translate-y-0 hover:bg-primary hover:text-white hover:shadow-[0_0_12px_hsl(215,80%,48%)] transition-all duration-[250ms]">
              <ChevronRight className="w-5 h-5" />
            </CarouselNext>
          </div>
        </Carousel>
      </div>
    </section>;
}